import { Flex, useMediaQuery, useTheme } from '@chakra-ui/react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import BECarousel from '@molecules/BECarousel';
import { useCallback, useEffect, useState } from 'react';
import { ThemeColors } from '@theme/foundations/colors';
import BEImage from '@molecules/BEImage/BEImage';
interface ResponsiveItemProps {
  itemWidth?: number;
  itemHeight: number;
  multiplier: number;
}
interface ResponsiveProps {
  [key: string]: ResponsiveItemProps;
  xs: ResponsiveItemProps;
  sm: ResponsiveItemProps;
  md: ResponsiveItemProps;
  lg: ResponsiveItemProps;
  xl: ResponsiveItemProps;
}

interface ImageProps {
  activeIndex?: number;
  index?: number;
  isSelectable?: boolean;
}

export interface BEImageListProps {
  direction?: 'horizontal' | 'vertical';
  gap?: number;
  images: string[];
  isSelectable?: boolean;
  onChange?: (activeIndex: number) => void;
  onClickItem?: (index: number) => void;
  activeIndex?: number;
  responsive?: ResponsiveProps;
  showDots?: boolean;
}

const StyledImage = styled(BEImage)<ImageProps>`
  width: 100%;
  height: 100%;
  ${({ activeIndex, index, isSelectable }) => {
    if (isSelectable && index === activeIndex) {
      return css`
        border-width: 2px;
        border-style: solid;
        border-color: ${ThemeColors.success['500']};
      `;
    }
  }}
  user-select: none;
  -webkit-user-drag: none;
`;

const DefaultResponsiveForVertical: ResponsiveProps = {
  xs: {
    itemHeight: 250,
    multiplier: 0.55,
  },
  sm: {
    itemHeight: 250,
    multiplier: 0.55,
  },
  md: {
    itemHeight: 160,
    multiplier: 0.55,
  },
  lg: {
    itemHeight: 160,
    multiplier: 0.55,
  },
  xl: {
    itemHeight: 160,
    multiplier: 0.55,
  },
  '2xl': {
    itemHeight: 160,
    multiplier: 0.55,
  },
};

const DefaultResponsiveForHorizontal: ResponsiveProps = {
  xs: {
    itemHeight: 200,
    multiplier: 0.75,
  },
  sm: {
    itemHeight: 200,
    multiplier: 0.75,
  },
  md: {
    itemWidth: 334,
    itemHeight: 200,
    multiplier: 0.65,
  },
  lg: {
    itemWidth: 295,
    itemHeight: 200,
    multiplier: 0.55,
  },
  xl: {
    itemWidth: 252,
    itemHeight: 200,
    multiplier: 0.45,
  },
  '2xl': {
    itemWidth: 217,
    itemHeight: 200,
    multiplier: 0.35,
  },
};

const useResponsive = (responsive: ResponsiveProps) => {
  const theme = useTheme();
  const breakpoints = theme.breakpoints;

  const [isSmallerThanXS] = useMediaQuery(`(max-width: ${breakpoints.xs})`);

  const [isBetweenXSAndSM] = useMediaQuery(
    `(min-width: ${breakpoints.xs}) and (max-width: ${breakpoints.sm})`
  );

  const [isBetweenSMAndMD] = useMediaQuery(
    `(min-width: ${breakpoints.sm}) and (max-width: ${breakpoints.md})`
  );

  const [isBetweenMDAndLG] = useMediaQuery(
    `(min-width: ${breakpoints.md}) and (max-width: ${breakpoints.lg})`
  );

  const [isBetweenLGAndXL] = useMediaQuery(
    `(min-width: ${breakpoints.lg}) and (max-width: ${breakpoints.xl})`
  );

  const [isGreaterThanXL] = useMediaQuery(`(min-width: ${breakpoints.xl})`);

  if (isSmallerThanXS) {
    return responsive['xs'];
  }

  if (isBetweenXSAndSM) {
    return responsive['sm'];
  }

  if (isBetweenSMAndMD) {
    return responsive['md'];
  }

  if (isBetweenMDAndLG) {
    return responsive['lg'];
  }

  if (isBetweenLGAndXL) {
    return responsive['xl'];
  }

  if (isGreaterThanXL) {
    return responsive['2xl'];
  }

  return responsive['xl'];
};

const BEImageList = ({
  activeIndex = 0,
  direction = 'horizontal',
  gap = 0,
  images,
  isSelectable = false,
  responsive,
  showDots = true,
  onChange,
  onClickItem,
}: BEImageListProps & ImageProps) => {
  const [constraint, setConstraint] = useState(0);
  const [sliderWidth, setSliderWidth] = useState(0);
  const [sliderHeight, setSliderHeight] = useState(0);
  const initSliderWidth = useCallback(
    (width: number) => setSliderWidth(width),
    []
  );
  const initSliderHeight = useCallback(
    (height: number) => setSliderHeight(height),
    []
  );

  const {
    itemWidth = sliderWidth - (direction === 'horizontal' ? gap : 0),
    itemHeight,
    multiplier,
  } = useResponsive(
    responsive ||
      (direction === 'horizontal'
        ? DefaultResponsiveForHorizontal
        : DefaultResponsiveForVertical)
  );

  useEffect(() => {
    if (itemWidth > 0 && itemHeight > 0) {
      if (direction === 'horizontal') {
        setConstraint(Math.floor(sliderWidth / (itemWidth + gap)));
      } else {
        setConstraint(Math.floor(sliderHeight / (itemHeight + gap)));
      }
    }
  }, [direction, gap, itemWidth, itemHeight, sliderWidth, sliderHeight]);
  return (
    <BECarousel
      activeIndex={activeIndex}
      gap={gap}
      isSelectable={isSelectable}
      itemWidth={itemWidth}
      itemHeight={itemHeight}
      multiplier={multiplier}
      constraint={constraint}
      showDots={showDots}
      direction={direction}
      onChange={onChange}
      onClickItem={onClickItem}
      onInitSliderWidth={initSliderWidth}
      onInitSliderHeight={initSliderHeight}
    >
      {images.map((image: any, index) => (
        <Flex
          key={image}
          shadow="md"
          w="100%"
          h="100%"
          borderRadius="8px"
          overflow="hidden"
        >
          <StyledImage src={image} index={index} fit="cover" />
        </Flex>
      ))}
    </BECarousel>
  );
};

export default BEImageList;
